/**
 * write-chain.ts: per-key promise serialization for store writes.
 *
 * Probe-clean leaf (ZERO runtime imports, the who-match.ts precedent) so
 * scripts/probe-write-chain.mjs can verify the serialization semantics
 * under tsx without dragging in zustand/idb.
 *
 * Consumed by store.ts `patchPrayer` to serialize read-compose-write
 * cycles per prayer id: the companions sheet's two match lanes (story +
 * verse) can settle within milliseconds of each other on a fresh detail
 * run, and unserialized read-then-write updatePrayer calls would let one
 * lane's full-object write clobber the other lane's just-landed field
 * (share-scripture-found arc, plan section 3).
 *
 * Cleanup is COMPARE-AND-DELETE (load-bearing): only the CURRENT tail
 * may remove its map entry. An unconditional delete would let write A's
 * settle remove the entry while write B is still the pending tail, so a
 * later write C would find no entry, chain onto nothing, and run
 * concurrently with B, reopening the exact clobber race this module
 * exists to close. Guarded by probe-write-chain.mjs test 2.
 */

export function chainKeyedWrite<K>(
  chains: Map<K, Promise<void>>,
  key: K,
  task: () => Promise<void>,
): Promise<void> {
  const prev = chains.get(key) ?? Promise.resolve();
  // A failed prior write must not poison the chain: chain past its
  // rejection. Each tail still propagates its OWN rejection to its own
  // caller via the returned promise.
  const tail = prev.catch(() => undefined).then(task);
  chains.set(key, tail);
  const cleanup = () => {
    if (chains.get(key) === tail) chains.delete(key);
  };
  // Observe both settle paths; the rejection arm also keeps this
  // internal observer from surfacing an unhandled rejection on tails
  // whose caller handles the error on the returned promise instead.
  tail.then(cleanup, cleanup);
  return tail;
}
